import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import { emailTemplate } from '../templates/emailTemplate';
import { useUser } from '../contexts/UserContext';

interface EmailShareDialogProps {
  open: boolean;
  onClose: () => void;
  selectedItinerary: {
    일정번호: number;
  } | null;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const EmailShareDialog: React.FC<EmailShareDialogProps> = ({
  open,
  onClose,
  selectedItinerary,
  onSuccess,
  onError
}) => {
  const { userInfo, companyInfo } = useUser();
  const [emailList, setEmailList] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageOption, setMessageOption] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!selectedItinerary) return;
    
    setIsSending(true);
    try {
      const mailBody = emailTemplate
        .replace('##메세지', messageOption)
        .replaceAll('#여행사명', userInfo?.업체명 || '랜드박스')
        .replace('##업체명', companyInfo?.업체명 || '랜드박스')
        .replace('##여행사주소', companyInfo?.기본주소  + ' ' + companyInfo?.상세주소 || '서울시 강남구')
        .replace('##대표자명', companyInfo?.대표자 || '홍길동')
        .replace('##대표이메일', companyInfo?.이메일 || 'support@landbox.com')
        .replace('#운영시간', '평일 09:00-18:00')
        .replace('##발송년도', new Date().getFullYear().toString())
        .replace('#url', `${window.location.origin}/preview/${selectedItinerary.일정번호}`)
        .replace('#logo', companyInfo?.회사로고 || '홍길동')
      console.log(companyInfo)
      const postData = {
        recipients: emailList.split(',').map(email => email.trim()),
        subject: messageSubject,
        body: mailBody,
        from: {
          name: companyInfo?.업체명 || '랜드박스',
          email: companyInfo?.이메일 || 'support@landbox.com'
        }
      };
      
      const response = await fetch('https://revato.mytripbox.co.kr/itinerary/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        throw new Error(`Email sending failed: ${response.status}`);
      }

      onSuccess();
      handleClose();
    } catch (error) {
      onError('이메일 전송에 실패했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setEmailList('');
    setMessageSubject('');
    setMessageOption('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>일정전송</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            최대 5개 이메일 주소 입력 가능 (쉼표로 구분)
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="받는 사람 이메일"
            type="email"
            fullWidth
            variant="outlined"
            value={emailList}
            onChange={(e) => setEmailList(e.target.value)}
            helperText="여러 이메일은 쉼표(,)로 구분해주세요"
          />
          <TextField
            margin="dense"
            label="제목"
            type="text"
            fullWidth
            variant="outlined"
            value={messageSubject}
            onChange={(e) => setMessageSubject(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="메시지(선택사항)"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={messageOption}
            onChange={(e) => setMessageOption(e.target.value)}
            sx={{ mt: 2 }}
          />
        </Box>
        <Box sx={{ mt: 2, fontSize: '12px', color: 'primary' }}>
          *일정표 다운로드와 미리보기 웹주소는 자동으로 첨부됩니다.
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>취소</Button>
        <Button 
          onClick={handleSend} 
          variant="contained" 
          disabled={isSending || !emailList || !messageSubject}
        >
          {isSending ? '전송 중...' : '전송'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailShareDialog;
